import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config';
import { useAuth } from '../context/AuthContext';
import { auth } from '../firebase';
import AppLayout from '../metronic/layouts/AppLayout';

const Settings = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('account');

  // Form states
  const [accountForm, setAccountForm] = useState({
    companyName: '',
    industry: '',
    companySize: '',
    location: '',
    email: '',
    phone: '',
    website: ''
  });

  const [subscriptionForm, setSubscriptionForm] = useState({
    tier: 'basic',
    status: 'active',
    creditsRemaining: 0,
    monthlyLimit: 1000,
    expiryDate: ''
  });

  const [aiForm, setAiForm] = useState({
    autonomyLevel: 'manual',
    scoreThreshold: 70,
    contactMethods: ['email'],
    maxCandidates: 50,
    retryAttempts: 3
  });

  const [calendlyForm, setCalendlyForm] = useState({
    calendlyUrl: '',
    meetingDuration: 30,
    bufferTime: 15,
    timezone: 'Asia/Singapore'
  });

  const [emailForm, setEmailForm] = useState({
    emailProvider: 'system',
    brevoApiKey: '',
    senderEmail: '',
    senderName: ''
  });

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      if (!currentUser) {
        console.log('No authenticated user, skipping settings fetch');
        return;
      }

      const user = auth.currentUser;
      const idToken = await user.getIdToken();
      const headers = {
        'Authorization': `Bearer ${idToken}`
      };

      // Fetch all settings from different endpoints
      const [profileRes, subscriptionRes, aiRes, calendlyRes, emailRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/settings/profile`, { headers }),
        fetch(`${API_BASE_URL}/api/settings/subscription`, { headers }),
        fetch(`${API_BASE_URL}/api/settings/ai-config`, { headers }),
        fetch(`${API_BASE_URL}/api/settings/calendly`, { headers }),
        fetch(`${API_BASE_URL}/api/settings/email-config`, { headers })
      ]);

      // Update forms with fetched data
      if (profileRes.ok) {
        const profileData = await profileRes.json();
        setAccountForm(profileData.account || {});
      } else {
        console.error('Failed to fetch profile settings:', profileRes.status, profileRes.statusText);
      }

      if (subscriptionRes.ok) {
        const subscriptionData = await subscriptionRes.json();
        setSubscriptionForm(subscriptionData.subscription || {});
      } else {
        console.error('Failed to fetch subscription settings:', subscriptionRes.status, subscriptionRes.statusText);
      }

      if (aiRes.ok) {
        const aiData = await aiRes.json();
        setAiForm(aiData.ai || {});
      } else {
        console.error('Failed to fetch AI settings:', aiRes.status, aiRes.statusText);
      }

      if (calendlyRes.ok) {
        const calendlyData = await calendlyRes.json();
        setCalendlyForm(calendlyData.calendly || {});
      } else {
        console.error('Failed to fetch Calendly settings:', calendlyRes.status, calendlyRes.statusText);
      }

      if (emailRes.ok) {
        const emailData = await emailRes.json();
        setEmailForm(emailData.email || {});
      } else {
        console.error('Failed to fetch email settings:', emailRes.status, emailRes.statusText);
      }

    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleAccountChange = (e) => {
    const { name, value } = e.target;
    setAccountForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubscriptionChange = (e) => {
    const { name, value } = e.target;
    setSubscriptionForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAiChange = (e) => {
    const { name, value, type, checked } = e.target;
    setAiForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleCalendlyChange = (e) => {
    const { name, value } = e.target;
    setCalendlyForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEmailChange = (e) => {
    const { name, value } = e.target;
    setEmailForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleContactMethodsChange = (methods) => {
    setAiForm(prev => ({
      ...prev,
      contactMethods: methods
    }));
  };

  const saveSettings = async (section) => {
    setSaving(true);
    try {
      let data = {};
      let endpoint = '';

      switch (section) {
        case 'account':
          data = accountForm;
          endpoint = `${API_BASE_URL}/api/settings/profile`;
          break;
        case 'subscription':
          data = subscriptionForm;
          endpoint = `${API_BASE_URL}/api/settings/subscription`;
          break;
        case 'ai':
          data = aiForm;
          endpoint = `${API_BASE_URL}/api/settings/ai-config`;
          break;
        case 'calendly':
          data = calendlyForm;
          endpoint = `${API_BASE_URL}/api/settings/calendly`;
          break;
        case 'email':
          data = emailForm;
          endpoint = `${API_BASE_URL}/api/settings/email-config`;
          break;
        default:
          return;
      }

      if (!currentUser) {
        toast.error('User not authenticated');
        return;
      }

      const user = auth.currentUser;
      const idToken = await user.getIdToken();
      const response = await fetch(endpoint, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify(data)
      });

      if (response.ok) {
        toast.success(`${section.charAt(0).toUpperCase() + section.slice(1)} settings saved successfully`);
      } else {
        throw new Error('Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { id: 'account', name: 'Account Profile', icon: '👤' },
    { id: 'subscription', name: 'Subscription', icon: '💳' },
    { id: 'ai', name: 'AI Settings', icon: '🤖' },
    { id: 'calendly', name: 'Calendly', icon: '📅' },
    { id: 'email', name: 'Email Configuration', icon: '✉️' }
  ];

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center py-24">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header + top tabs */}
        <div className="bg-white rounded-t-lg shadow-sm border border-b-0">
          <div className="px-6 pt-6 pb-4 flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">Settings</h1>
              <p className="mt-1 text-sm text-gray-500">
                Configure your account, subscription, AI behavior, scheduling, and email.
              </p>
            </div>
          </div>
          <div className="px-6">
            <nav className="-mb-px flex space-x-6 overflow-x-auto">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`whitespace-nowrap pb-3 border-b-2 text-sm font-medium flex items-center ${
                    activeTab === tab.id
                      ? 'border-orange-500 text-orange-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <span className="mr-2">{tab.icon}</span>
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-b-lg shadow-sm border border-t-0">
              {/* Account Profile Tab */}
              {activeTab === 'account' && (
                <div className="p-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-6">Account Profile</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Company Name
                      </label>
                      <input
                        type="text"
                        name="companyName"
                        value={accountForm.companyName}
                        onChange={handleAccountChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Industry
                      </label>
                      <select
                        name="industry"
                        value={accountForm.industry}
                        onChange={handleAccountChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      >
                        <option value="">Select Industry</option>
                        <option value="technology">Technology</option>
                        <option value="finance">Finance</option>
                        <option value="healthcare">Healthcare</option>
                        <option value="manufacturing">Manufacturing</option>
                        <option value="retail">Retail</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Company Size
                      </label>
                      <select
                        name="companySize"
                        value={accountForm.companySize}
                        onChange={handleAccountChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      >
                        <option value="">Select Size</option>
                        <option value="1-10">1-10 employees</option>
                        <option value="11-50">11-50 employees</option>
                        <option value="51-200">51-200 employees</option>
                        <option value="201-500">201-500 employees</option>
                        <option value="500+">500+ employees</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Location
                      </label>
                      <input
                        type="text"
                        name="location"
                        value={accountForm.location}
                        onChange={handleAccountChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                        placeholder="City, Country"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={accountForm.email}
                        onChange={handleAccountChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Phone
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={accountForm.phone}
                        onChange={handleAccountChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Website
                      </label>
                      <input
                        type="url"
                        name="website"
                        value={accountForm.website}
                        onChange={handleAccountChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                        placeholder="https://example.com"
                      />
                    </div>
                  </div>
                  <div className="mt-6 flex justify-end">
                    <button
                      onClick={() => saveSettings('account')}
                      disabled={saving}
                      className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:opacity-50"
                    >
                      {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </div>
              )}

              {/* Subscription Tab */}
              {activeTab === 'subscription' && (
                <div className="p-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-6">Subscription Management</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Subscription Tier
                      </label>
                      <select
                        name="tier"
                        value={subscriptionForm.tier}
                        onChange={handleSubscriptionChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      >
                        <option value="basic">Basic</option>
                        <option value="professional">Professional</option>
                        <option value="enterprise">Enterprise</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Status
                      </label>
                      <div className="flex items-center">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          subscriptionForm.status === 'active'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {subscriptionForm.status}
                        </span>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Credits Remaining
                      </label>
                      <div className="text-2xl font-bold text-orange-600">
                        {subscriptionForm.creditsRemaining}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Monthly Limit
                      </label>
                      <input
                        type="number"
                        name="monthlyLimit"
                        value={subscriptionForm.monthlyLimit}
                        onChange={handleSubscriptionChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Expiry Date
                      </label>
                      <input
                        type="date"
                        name="expiryDate"
                        value={subscriptionForm.expiryDate}
                        onChange={handleSubscriptionChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      />
                    </div>
                  </div>
                  <div className="mt-6 flex justify-end space-x-3">
                    <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500">
                      Purchase Credits
                    </button>
                    <button
                      onClick={() => saveSettings('subscription')}
                      disabled={saving}
                      className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:opacity-50"
                    >
                      {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </div>
              )}

              {/* AI Settings Tab */}
              {activeTab === 'ai' && (
                <div className="p-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-6">AI Settings</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Autonomy Level
                      </label>
                      <select
                        name="autonomyLevel"
                        value={aiForm.autonomyLevel}
                        onChange={handleAiChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      >
                        <option value="manual">Manual</option>
                        <option value="semi-auto">Semi-Auto</option>
                        <option value="full-auto">Full-Auto</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Score Threshold
                      </label>
                      <input
                        type="number"
                        name="scoreThreshold"
                        value={aiForm.scoreThreshold}
                        onChange={handleAiChange}
                        min="0"
                        max="100"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Max Candidates per Job
                      </label>
                      <input
                        type="number"
                        name="maxCandidates"
                        value={aiForm.maxCandidates}
                        onChange={handleAiChange}
                        min="1"
                        max="100"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Retry Attempts
                      </label>
                      <input
                        type="number"
                        name="retryAttempts"
                        value={aiForm.retryAttempts}
                        onChange={handleAiChange}
                        min="0"
                        max="5"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Contact Methods
                      </label>
                      <div className="space-y-2">
                        {['email', 'sms', 'whatsapp', 'voice'].map((method) => (
                          <label key={method} className="flex items-center">
                            <input
                              type="checkbox"
                              checked={aiForm.contactMethods.includes(method)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  handleContactMethodsChange([...aiForm.contactMethods, method]);
                                } else {
                                  handleContactMethodsChange(aiForm.contactMethods.filter(m => m !== method));
                                }
                              }}
                              className="mr-2"
                            />
                            <span className="capitalize">{method}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="mt-6 flex justify-end">
                    <button
                      onClick={() => saveSettings('ai')}
                      disabled={saving}
                      className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:opacity-50"
                    >
                      {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </div>
              )}

              {/* Calendly Tab */}
              {activeTab === 'calendly' && (
                <div className="p-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-6">Calendly Integration</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Calendly URL
                      </label>
                      <input
                        type="url"
                        name="calendlyUrl"
                        value={calendlyForm.calendlyUrl}
                        onChange={handleCalendlyChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                        placeholder="https://calendly.com/your-username"
                      />
                      <p className="mt-1 text-sm text-gray-500">
                        Enter your Calendly scheduling link for candidate interviews
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Meeting Duration (minutes)
                      </label>
                      <select
                        name="meetingDuration"
                        value={calendlyForm.meetingDuration}
                        onChange={handleCalendlyChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      >
                        <option value={15}>15 minutes</option>
                        <option value={30}>30 minutes</option>
                        <option value={45}>45 minutes</option>
                        <option value={60}>60 minutes</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Buffer Time (minutes)
                      </label>
                      <select
                        name="bufferTime"
                        value={calendlyForm.bufferTime}
                        onChange={handleCalendlyChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      >
                        <option value={0}>No buffer</option>
                        <option value={5}>5 minutes</option>
                        <option value={10}>10 minutes</option>
                        <option value={15}>15 minutes</option>
                        <option value={30}>30 minutes</option>
                      </select>
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Timezone
                      </label>
                      <select
                        name="timezone"
                        value={calendlyForm.timezone}
                        onChange={handleCalendlyChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      >
                        <option value="Asia/Singapore">Asia/Singapore</option>
                        <option value="America/New_York">America/New_York</option>
                        <option value="America/Los_Angeles">America/Los_Angeles</option>
                        <option value="Europe/London">Europe/London</option>
                        <option value="Europe/Paris">Europe/Paris</option>
                        <option value="Asia/Tokyo">Asia/Tokyo</option>
                        <option value="Australia/Sydney">Australia/Sydney</option>
                      </select>
                    </div>
                  </div>
                  <div className="mt-6 flex justify-end">
                    <button
                      onClick={() => saveSettings('calendly')}
                      disabled={saving}
                      className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:opacity-50"
                    >
                      {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </div>
              )}

              {/* Email Configuration Tab */}
              {activeTab === 'email' && (
                <div className="p-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-6">Email Configuration</h2>

                  {/* Info Alert */}
                  <div className="mb-6 p-4 bg-blue-50 border-l-4 border-blue-500 rounded">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <span className="text-blue-500 text-xl">ℹ️</span>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-blue-900">Email Sending Options</h3>
                        <p className="mt-1 text-sm text-blue-700">
                          Choose to send emails from your own company email (requires Brevo account) or use the system default.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-6">
                    {/* Email Provider Selection */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Email Provider
                      </label>
                      <div className="space-y-2">
                        <label className="flex items-center p-4 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                          <input
                            type="radio"
                            name="emailProvider"
                            value="system"
                            checked={emailForm.emailProvider === 'system'}
                            onChange={handleEmailChange}
                            className="mr-3"
                          />
                          <div>
                            <div className="font-medium text-gray-900">Use System Email</div>
                            <div className="text-sm text-gray-500">Emails sent from kevin@candid-i.com (default)</div>
                          </div>
                        </label>
                        <label className="flex items-center p-4 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                          <input
                            type="radio"
                            name="emailProvider"
                            value="custom"
                            checked={emailForm.emailProvider === 'custom'}
                            onChange={handleEmailChange}
                            className="mr-3"
                          />
                          <div>
                            <div className="font-medium text-gray-900">Use My Own Email</div>
                            <div className="text-sm text-gray-500">Send from your company email (requires Brevo account)</div>
                          </div>
                        </label>
                      </div>
                    </div>

                    {/* Custom Email Configuration */}
                    {emailForm.emailProvider === 'custom' && (
                      <>
                        <div className="p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded">
                          <p className="text-sm text-yellow-800">
                            <strong>Setup Required:</strong> You need a Brevo account with a verified sender email.
                            <a href="https://www.brevo.com" target="_blank" rel="noopener noreferrer" className="underline ml-1">
                              Sign up for free →
                            </a>
                          </p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Brevo API Key *
                          </label>
                          <input
                            type="password"
                            name="brevoApiKey"
                            value={emailForm.brevoApiKey}
                            onChange={handleEmailChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                            placeholder="xkeysib-..."
                          />
                          <p className="mt-1 text-sm text-gray-500">
                            Get your API key from Brevo Dashboard → Account → SMTP & API → API Keys
                          </p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Sender Email *
                          </label>
                          <input
                            type="email"
                            name="senderEmail"
                            value={emailForm.senderEmail}
                            onChange={handleEmailChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                            placeholder="derrick@yourcompany.com"
                          />
                          <p className="mt-1 text-sm text-gray-500">
                            ⚠️ This email must be verified in your Brevo account
                          </p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Sender Name
                          </label>
                          <input
                            type="text"
                            name="senderName"
                            value={emailForm.senderName}
                            onChange={handleEmailChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                            placeholder="Your Name"
                          />
                          <p className="mt-1 text-sm text-gray-500">
                            Name that appears in the "From" field (optional - defaults to company name)
                          </p>
                        </div>
                      </>
                    )}
                  </div>

                  <div className="mt-6 flex justify-end">
                    <button
                      onClick={() => saveSettings('email')}
                      disabled={saving}
                      className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:opacity-50"
                    >
                      {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
      </AppLayout>
  );
};

export default Settings;

